import React from 'react';

// Shared vocabulary, helpers and small display components for the supplier
// pages (list/grid/map, detail, new). Single source of truth — the standing
// colours, QPF axes and star rendering must look identical in every view.

export const ACCENT = '#f59e0b';

export const STANDING_COLORS = {
  Preferred: '#34d399', Approved: '#60a5fa', Active: '#94a3b8',
  Conditional: '#f59e0b', 'On Hold': '#f97316', Disqualified: '#EC2124', Prospect: '#a78bfa',
};

// Quick decision triad: quality = workmanship, price = cost-competitiveness
// (higher = better value), speed = lead time / responsiveness (higher = faster).
export const QPF_AXES = [
  { key: 'quality', label: 'Quality', short: 'Q', color: '#34d399' },
  { key: 'price', label: 'Price', short: 'P', color: '#60a5fa' },
  { key: 'speed', label: 'Speed', short: 'S', color: '#f59e0b' },
];

export const num = (v) => (v === '' || v === null || v === undefined ? null : Number(v));

export const supplierIdentityMatches = (supplier, query) => {
  const term = String(query || '').trim().toLowerCase();
  return !term || [supplier.name, supplier.legalName, supplier.documentNumber, supplier.tripletexSupplierNumber, supplier.supplierKey]
    .filter(Boolean).some(value => String(value).toLowerCase().includes(term));
};

// Trimmed, de-duplicated capability categories of a supplier.
export const capsOf = (s) => {
  const out = [];
  (s.capabilities || []).forEach(c => {
    const cat = (c.category || '').trim();
    if (cat && !out.includes(cat)) out.push(cat);
  });
  return out;
};

export const capabilitiesFor = (s, cat) => (s.capabilities || []).filter(c => (c.category || '').trim() === cat);

export const capabilityFor = (s, cat) =>
  (s.capabilities || []).find(c => (c.category || '').trim() === cat);

// A supplier's rating for one capability — null when unrated (0 counts as unrated).
export const ratingOf = (s, cat) => {
  const cap = capabilityFor(s, cat);
  const r = cap && cap.rating != null ? Math.round(Number(cap.rating)) : 0;
  return r > 0 ? r : null;
};

export const starString = (r) => (r == null ? '' : '★'.repeat(r) + '☆'.repeat(Math.max(0, 5 - r)));

export function Stars({ value }) {
  if (value == null || value === '') return <span style={{ color: 'var(--ni-text-muted)' }}>—</span>;
  const v = Math.round(Number(value));
  return <span style={{ color: ACCENT, letterSpacing: 1 }} title={`${value} / 5`}>{starString(v)}</span>;
}

export function StandingBadge({ standing }) {
  const c = STANDING_COLORS[standing] || '#666';
  return (
    <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 20, fontSize: 11,
      fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', background: c + '22', color: c }}>
      {standing || '—'}
    </span>
  );
}

// Compact Quality / Price / Speed chips; a single muted dash when nothing is rated.
export function Triad({ qpf }) {
  const q = qpf || {};
  if (!QPF_AXES.some(a => q[a.key] != null)) return <span style={{ color: 'var(--ni-text-muted)', fontSize: 12 }} title="Not rated yet">—</span>;
  return (
    <span style={{ display: 'inline-flex', gap: 4 }}>
      {QPF_AXES.map(a => (
        <span key={a.key} title={`${a.label} ${q[a.key] ?? '–'} / 5`} style={{ fontSize: 11, fontWeight: 700, padding: '1px 6px', borderRadius: 10, background: a.color + '1f', color: a.color }}>
          {a.short}{q[a.key] != null ? q[a.key] : '–'}
        </span>
      ))}
    </span>
  );
}

// A <select> from a fixed vocabulary that still preserves an existing custom value.
export function VocabSelect({ value, options, onChange, placeholder, style }) {
  const opts = value && !options.includes(value) ? [value, ...options] : options;
  return (
    <select value={value || ''} onChange={e => onChange(e.target.value)} style={style}>
      <option value="">{placeholder || '—'}</option>
      {opts.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}
